import { Field, Form, Formik, type FormikHelpers } from 'formik';
import { ChangeEvent, useRef } from 'react';
import { CiMicrophoneOn } from 'react-icons/ci';

import { useAutosizeTextarea } from '../../hooks/useAutosizeTextarea';
import { useSpeechToText } from '../../hooks/useSpeechToText';
import Button from '../../ui/button';
import LongArrowIcon from './long-arrow-icon';

interface Props {
  isProcessing: boolean;
  handleSubmit: (
    values: { query: string },
    { resetForm }: FormikHelpers<{ query: string }>,
  ) => void;
  handleKeyDown: (event: KeyboardEvent, submitForm: () => void) => void;
}

const QuestionForm = ({ handleKeyDown, handleSubmit, isProcessing }: Props) => {
  return (
    <Formik initialValues={{ query: '' }} onSubmit={() => {}}>
      {(formik) => {
        const { values, handleChange, submitForm, setFieldValue, resetForm } = formik;

        const textareaRef = useRef<HTMLTextAreaElement | null>(null);

        useAutosizeTextarea(textareaRef, values.query);

        const {
          isRecording,
          start,
          stop,
          syncFromInput,
          reset: resetSpeech,
        } = useSpeechToText((text) => setFieldValue('query', text), textareaRef);

        const handleSubmitInternal = () => {
          submitForm();
          handleSubmit(values, formik);
          resetForm();
          resetSpeech();
        };

        return (
          <Form
            className="p-3 border border-solid border-lightGray rounded-lg relative flex items-end gap-2"
            style={{
              backgroundColor: 'var(--background)',
              color: 'var(--foreground)',
            }}
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmitInternal();
            }}
          >
            {/* micro */}
            <Button
              type="button"
              onClick={isRecording ? stop : start}
              className={`!p-0 min-h-[36px] min-w-[36px] ${
                isRecording ? '!bg-red-500' : '!bg-blue-600'
              } !text-white`}
            >
              <CiMicrophoneOn className="h-[22px] w-[22px]" />
            </Button>

            {/* textarea */}
            <Field
              innerRef={textareaRef}
              as="textarea"
              name="query"
              rows={1}
              value={values.query}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
                handleChange(e);
                syncFromInput(e.target.value);
              }}
              onKeyDown={(e: KeyboardEvent) => handleKeyDown(e, handleSubmitInternal)}
              className="w-full text-sm resize-none overflow-y-auto focus:outline-none p-2 leading-relaxed max-h-[200px]"
            />

            {/* submit */}
            <Button
              type="submit"
              disabled={isProcessing || !values.query.trim()}
              className="!p-0 !min-w-9 !h-9 !bg-blue-600 !text-white"
            >
              <LongArrowIcon className="rotate-90" />
            </Button>
          </Form>
        );
      }}
    </Formik>
  );
};

export default QuestionForm;
